import { z } from "zod";
import {
  getImportedProfile,
  restoreLegacyDocuments,
} from "./legacyDocumentRestore";
import type { QuerySecurity } from "@/api/queries/types";
import type { Portfolio } from "@/api/types";
import type {
  ContactEntry,
  DocProducerOverrides,
} from "@/providers/DocProducerProvider";
import type { DocProducerDocument } from "@/views/docproducer";

interface DocumentRestore {
  version: number;
  contactId: string;
  documents: DocProducerDocument[];
  modData: Record<string, unknown>;
  overrides: DocProducerOverrides;
  warnings?: string[];
}

const record = z.record(z.string(), z.unknown());
const restoreSchema = z.object({
  version: z.number().refine((value) => value === 1, {
    message: "Ukjent versjon av gjenopprettingsformatet.",
  }),
  contactId: z.string().min(1),
  documents: z.array(
    z.object({
      id: z.string().min(1),
      docType: z.string().min(1),
      processName: z.string(),
      desc: z.string(),
      data: record,
    })
  ),
  modData: record,
  overrides: z.object({
    profile: z.record(z.string(), z.string()),
    sourceDocIds: z.array(z.string()),
    afterSign: record.nullable(),
    docProdOut: record.nullable().optional(),
  }),
});

const uploadedSchema = z.object({
  file: z.object({
    name: z.string().min(1),
    type: z.string(),
    lastModified: z.number(),
  }),
  stream: z.string().regex(/^data:[^,]*;base64,/),
});

export function createDocumentRestore(entry: ContactEntry) {
  return {
    version: 1,
    contactId: String(entry.id),
    documents: entry.documents.documents,
    modData: entry.modData,
    overrides: entry.docProducerOverrides ?? {
      profile: {},
      sourceDocIds: [],
      afterSign: null,
      docProdOut: null,
    },
  };
}

export function documentJsonReplacer(_key: string, value: unknown): unknown {
  return value instanceof File
    ? { name: value.name, type: value.type, lastModified: value.lastModified }
    : value;
}

export function parseDocumentRestore(
  json: string,
  contactId: string,
  portfolios: Portfolio[] = [],
  securities: QuerySecurity[] = []
): DocumentRestore {
  const parsed: unknown = JSON.parse(json);
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    !("restore" in parsed)
  ) {
    return restoreLegacyDocuments(parsed, contactId, portfolios, securities);
  }
  const envelope = z.object({ restore: restoreSchema }).safeParse(parsed);
  if (!envelope.success) {
    const details: string[] = [];
    for (const issue of envelope.error.issues) {
      details.push(`${issue.path.join(".")}: ${issue.message}`);
      if (details.length === 3) break;
    }
    throw new Error(
      `Ugyldige gjenopprettingsdata: ${details.join("; ")}`
    );
  }
  const restore: DocumentRestore = envelope.data.restore;
  if (restore.contactId !== contactId) {
    throw new Error("JSON-filen tilhører en annen kunde.");
  }
  const ids = new Set(restore.documents.map((document) => document.id));
  if (ids.size !== restore.documents.length) {
    throw new Error("JSON-filen inneholder dokumenter med samme ID.");
  }
  const documents = restore.documents.map((document) => {
    if (document.docType === "order") {
      const data: Record<string, unknown> = { ...document.data };
      for (const key of ["existData", "newData"]) {
        const values = data[key];
        if (typeof values !== "object" || values === null || Array.isArray(values)) {
          continue;
        }
        const inputs: Record<string, unknown> = { ...values };
        for (let index = 1; index <= 6; index++) {
          const field = `InstrumentAmt${index}`;
          const value = inputs[field];
          if (typeof value === "string" && value.trim() === "") {
            delete inputs[field];
          }
        }
        data[key] = inputs;
      }
      return { ...document, data };
    }
    if (document.docType !== "uploaded") return document;
    const uploaded = uploadedSchema.safeParse(document.data);
    if (!uploaded.success) {
      throw new Error(`Vedlegget ${document.desc} har ugyldige fildata.`);
    }
    const { file, stream } = uploaded.data;
    const bytes = Uint8Array.from(atob(stream.slice(stream.indexOf(",") + 1)), (c) =>
      c.charCodeAt(0)
    );
    return {
      ...document,
      data: {
        ...document.data,
        stream,
        file: new File([bytes], file.name, {
          type: file.type,
          lastModified: file.lastModified,
        }),
      },
    };
  });
  return {
    ...restore,
    documents,
    overrides: {
      ...restore.overrides,
      profile: {
        ...getImportedProfile(parsed),
        ...restore.overrides.profile,
      },
    },
  };
}
